import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, DollarSign, CheckCircle2, Lightbulb, HardHat } from "lucide-react";

const BathroomCostGuidePage = () => {
  const costRanges = [
    {
      type: "Basic Renovation",
      range: "$5,000 – $12,000",
      description: "Cosmetic improvements without major structural work.",
      features: [
        "New paint and lighting",
        "Replacing sink, faucet, or toilet",
        "Vinyl or ceramic flooring",
        "Low-cost vanity and mirror",
        "Minor plumbing or electrical updates",
      ],
    },
    {
      type: "Mid-Range Renovation",
      range: "$12,000 – $25,000",
      description: "A more complete upgrade with improved finishes and functionality.",
      features: [
        "New bathtub or tiled shower",
        "Upgraded flooring and wall tiles",
        "Quartz or granite countertops",
        "Semi-custom cabinetry",
        "Modern fixtures and lighting upgrades",
      ],
      featured: true,
    },
    {
      type: "High-End Renovation",
      range: "$25,000 – $50,000+",
      description: "Luxury remodels with custom design, structural changes, and high-end materials.",
      features: [
        "Heated floors and towel racks",
        "Frameless glass showers or freestanding tubs",
        "Custom vanities and premium countertops",
        "Built-in lighting and smart tech",
        "Expanded floorplans or reconfigured layouts",
      ],
    },
  ];

  const costFactors = [
    { title: "Bathroom Size", description: "Larger bathrooms require more materials and labor." },
    { title: "Material Selection", description: "Natural stone, imported tiles, and designer fixtures drive up costs." },
    { title: "Labor", description: "Skilled tradespeople in Fraser Valley may charge higher rates during peak seasons." },
    { title: "Permits & Inspections", description: "Required for electrical, plumbing, or structural work." },
    { title: "Custom Work", description: "Built-in storage, unique layouts, or barrier-free designs often increase costs." },
    { title: "Plumbing & Electrical Changes", description: "Moving or upgrading systems can add thousands to the total." },
  ];

  const budgetTips = [
    { title: "Plan Ahead", description: "Finalize your design and scope before starting work." },
    { title: "Reuse What You Can", description: "Refinish tubs or reuse cabinetry when possible." },
    { title: "Stick to Standard Sizes", description: "Custom components cost more and take longer to install." },
    { title: "Hire the Right Contractor", description: "Avoid costly mistakes by working with experienced professionals." },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link 
            to="/services#bathroom" 
            className="inline-flex items-center gap-2 text-[#d97706] hover:text-[#b45309] mb-6 transition-colors"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
          <div className="max-w-3xl">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Renovation Guide
            </p>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6">
              Bathroom Renovation Costs in Fraser Valley
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl">
              A bathroom renovation is a smart way to improve your home's comfort, value, and functionality. Understanding the costs involved is key to planning your budget effectively.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <p className="text-lg text-slate-600 leading-relaxed">
            Whether you're updating a small powder room or remodeling a full ensuite, several factors can influence the final price tag. From materials and labor to permits and custom features, we'll break down the average cost of a bathroom remodel in Fraser Valley, explore cost drivers, and offer tips to make the most of your renovation investment.
          </p>
        </div>
      </section>

      {/* Cost Ranges */}
      <section className="py-16 md:py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Cost Breakdown
            </p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0f172a] uppercase tracking-tight">
              Average Cost by Renovation Type
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {costRanges.map((item, index) => (
              <div 
                key={index}
                className={`bg-white p-8 border ${item.featured ? 'border-[#d97706] border-2' : 'border-slate-200'} relative`}
                data-testid={`cost-range-${index}`}
              >
                {item.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d97706] text-white px-4 py-1 text-sm font-['Barlow_Condensed'] font-bold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0f172a] uppercase mb-2">
                    {item.type}
                  </h3>
                  <p className="font-['Barlow_Condensed'] text-3xl font-bold text-[#d97706]">
                    {item.range}
                  </p>
                </div>
                <p className="text-slate-600 text-center mb-6">
                  {item.description}
                </p>
                <ul className="space-y-3">
                  {item.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#d97706] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8">
            The final cost depends on factors like bathroom size, fixtures, tile choices, and whether plumbing or electrical systems need upgrades.
          </p>
        </div>
      </section>

      {/* Cost Factors */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              What Affects Your Budget
            </p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0f172a] uppercase tracking-tight">
              Factors That Influence Costs
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {costFactors.map((factor, index) => (
              <div 
                key={index}
                className="p-6 bg-[#f8fafc] border border-slate-200"
                data-testid={`cost-factor-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#d97706]/10 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-[#d97706]" />
                  </div>
                  <div>
                    <h3 className="font-['Barlow_Condensed'] font-bold text-[#0f172a] uppercase mb-1">
                      {factor.title}
                    </h3>
                    <p className="text-slate-600 text-sm">{factor.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Budget Tips */}
      <section className="py-16 md:py-24 bg-[#f1f5f9]">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Expert Advice
            </p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0f172a] uppercase tracking-tight">
              Tips to Stay on Budget
            </h2>
          </div>

          <div className="space-y-4">
            {budgetTips.map((tip, index) => (
              <div 
                key={index}
                className="bg-white p-6 flex items-start gap-4"
                data-testid={`budget-tip-${index}`}
              >
                <div className="w-10 h-10 bg-[#d97706] flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-['Barlow_Condensed'] font-bold text-[#0f172a] uppercase mb-1">
                    {tip.title}
                  </h3>
                  <p className="text-slate-600">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Choosing Contractor */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="w-16 h-16 bg-[#d97706]/10 flex items-center justify-center mx-auto mb-6">
            <HardHat className="w-8 h-8 text-[#d97706]" />
          </div>
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0f172a] uppercase tracking-tight mb-6">
            Choosing the Right Contractor
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            Choosing the right contractor is one of the most important decisions you'll make during your renovation. Look for licensed, insured, and experienced companies that specialize in bathroom renovations in Fraser Valley. By planning carefully and hiring the right contractor, you can transform your bathroom into a beautiful, functional space that adds long-term value to your home.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
            Ready to Start Your Bathroom Renovation?
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Contact JN Nickel Contracting Inc. for a free consultation and detailed quote tailored to your vision and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-10 btn-glow"
                data-testid="guide-cta-btn"
              >
                Get a Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/services#bathroom">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0f172a] font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-10"
                data-testid="guide-services-btn"
              >
                View Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BathroomCostGuidePage;
